<template>
    <modal
        :id="modalId"
        :title="propertyName + ' ' + 'Locks'"
        hide-footer
        @hide="handleModalHide"
    >
        <busy-modal :show="loading" :overlay="true"></busy-modal>
        <div class="resort-lock-locks" :style="{ height: '300px' }">
            <div class="resort-lock-locks__toolbar">
                <b-button
                    class="align-baseline"
                    title="Add Lock"
                    variant="default"
                    @click="handleAddLockClick"
                >
                    <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                </b-button>
                <b-button
                    class="align-baseline"
                    title="Edit Lock"
                    variant="default"
                    :disabled="!isDeleteEditEnabled"
                    @click="handleEditLockClick"
                >
                    <font-awesome-icon :icon="['fas', 'pen-to-square']"></font-awesome-icon>
                </b-button>
                <b-button
                    class="align-baseline"
                    title="Delete Lock"
                    variant="default"
                    :disabled="!isDeleteEditEnabled"
                    v-b-modal="deleteModalID"
                >
                    <font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
                </b-button>
                <b-button
                    class="align-baseline"
                    title="Fetch Locks"
                    variant="default"
                    @click="handleSyncLocksClick"
                >
                    <font-awesome-icon :icon="['fas', 'arrows-rotate']"></font-awesome-icon>
                </b-button>
            </div>
            <grid
                ref="grid"
                class="resort-lock-locks__grid"
                row-selection="single"
                :column-defs="columnsDefs"
                :is-search="false"
                :row-data="locks"
                @selection-changed="handleRowSelect"
            ></grid>
        </div>
        <resort-lock-lock-detail-modal-form
            :modal-id="addModalID"
            :create="createLock"
            @success-save="load"
        />
        <resort-lock-lock-detail-modal-form
            :modal-id="editModalID"
            :update="updateLock"
            :initial-values="currentLockInitialValues"
            @success-save="load"
        />
        <resort-lock-lock-delete-modal
            :modal-id="deleteModalID"
            :name="currentLockSelected && currentLockSelected.name || ''"
            @delete="handleDeleteLock"
        />
    </modal>
</template>
<script>
    import {
        DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_FETCH_LOCKS_ENDPOINT,
        DOOR_LOCKS_RESORT_LOCK_LOCK_BY_ID_ENDPOINT,
    } from '../../../../../endpoints';
    import API from '../../../../../api';
    import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
    import Grid from '../../../../generic/grid/grid.vue';
    import ResponsiveButton from '../../../../generic/responsive-button/responsive-button.vue';
    import Modal from '../../../../generic/modal/modal.vue';
    import ResortLockLockDetailModalForm from './resort-lock-lock-detail-modal-form.vue';
    import ResortLockLockDeleteModal from './resort-lock-lock-delete-modal.vue';

    export default {
        name: 'ResortLockLockListModal',
        components: {
            BusyModal,
            Modal,
            Grid,
            ResponsiveButton,
            ResortLockLockDetailModalForm,
            ResortLockLockDeleteModal,
        },
        props: {
            modalId: String,
            propertyName: {
                type: String,
                default: '',
            },
            propertyId: {
                type: Number,
                default: null
            }
        },
        computed: {
            isDeleteEditEnabled() {
                return !!this.currentLockSelected;
            },
            currentLockInitialValues() {
                if (!!this.currentLock) {
                    return {
                        id: this.currentLock.id,
                        name: this.currentLock.name,
                        model_id: this.currentLock.model_id,
                        mac: this.currentLock.mac,
                    }
                }
            },
        },
        data() {
            return {
                loading: false,
                addModalID: 'resort-lock-add-lock-modal',
                editModalID: 'resort-lock-update-lock-modal',
                deleteModalID: 'resort-lock-delete-lock-modal',
                locks: [],
                currentLock: null,
                currentLockSelected: null,
                columnsDefs: [
                    {
                        field: 'name',
                        headerName: 'LockState Property'
                    },
                    {
                        field: 'mac',
                        headerName: 'Serial #'
                    },
                ],
            }
        },
        methods: {
            handleAddLockClick() {
                this.$bvModal.show(this.addModalID);
            },
            handleEditLockClick() {
                this.currentLock = this.currentLockSelected;
                this.$bvModal.show(this.editModalID);
            },
            handleRowSelect() {
                const gridApi = this.$refs.grid.gridOptions.api;
                const selectedRows = gridApi.getSelectedRows();

                this.currentLockSelected = selectedRows.length
                    ? selectedRows[0]
                    : null;
            },
            handleModalHide() {
                this.$emit('hide');
            },
            createLock(payload) {
                return API.post(DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT(this.propertyId), payload);
            },
            updateLock({ id, ...payload }) {
                return API.put(DOOR_LOCKS_RESORT_LOCK_LOCK_BY_ID_ENDPOINT(this.propertyId, id), payload);
            },
            async handleDeleteLock(data) {
                this.loading = true;

                try {
                    await this.deleteLock(data);

                    this.currentLock = null;
                    this.currentLockSelected = null;
                    await this.fetchLocks();
                } finally {
                    this.loading = false;
                }
            },
            async handleSyncLocksClick() {
                this.loading = true;

                try {
                    await this.syncLocks();
                } finally {
                    this.loading = false;
                }
            },
            async load() {
                this.loading = true;

                try {
                    await this.fetchLocks();
                    this.currentLockSelected = null;
                } finally {
                    this.loading = false;
                }
            },
            async deleteLock(data) {
                try {
                    await API.delete(
                        DOOR_LOCKS_RESORT_LOCK_LOCK_BY_ID_ENDPOINT(this.propertyId, this.currentLockSelected.id),
                        { data }
                    );
                } catch {
                    this.errorToast({text: `Could not delete lock.`})
                }
            },
            async fetchLocks() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_LOCKS_ENDPOINT(this.propertyId));
                    this.locks = response.data;
                } catch {
                    this.errorToast({text: `Could not fetch locks.`})
                }
            },
            async syncLocks() {
                try {
                    const response = await API.get(DOOR_LOCKS_RESORT_LOCK_FETCH_LOCKS_ENDPOINT(this.propertyId));
                    const count = response.data.count;
                    this.successToast({text: `Fetched ${count} ${count === 1 ? 'lock' : 'locks'}.`})
                    await this.fetchLocks();
                } catch {
                    this.errorToast({text: `Could not fetch locks.`})
                }
            },
        },
        watch: {
            propertyId() {
                this.currentLock = null;
                if (this.propertyId) {
                    this.load();
                }
            }
        }
        
    }
</script>

<style lang="scss" scoped>
    .resort-lock-locks {
        display: flex;
        height: 100%;

        &__toolbar {
            border-right: 1px solid #dee2e6;
            margin-right: 5px;
            display: flex;
            flex-direction: column;
            padding-right: 5px;

            > button {
                margin: 2px;
            }
        }

        &__grid {
            width: 100%;
        }
    }
</style>
